import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ThreeSixty as ThreeSixtyIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { getCategoryGradient, handleCall, handleWhatsApp, handleShare } from "./utils";
import TileViewer360 from "./Tiles/TileViewer360";

interface CategoryPageTemplateProps {
  title: string;
  subtitle: string;
  description: string;
  categoryData: any[];
  categoryType: string;
  gradientCategory: string;
  icon: React.ElementType;
  features: Array<{
    icon: React.ElementType;
    text: string;
  }>;
}

const CategoryPageTemplate: React.FC<CategoryPageTemplateProps> = ({
  title,
  subtitle,
  description,
  categoryData,
  categoryType,
  gradientCategory,
  icon: IconComponent,
  features,
}) => {
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [view360, setView360] = useState<any>(null);

  const handle360View = (item: any) => {
    if (item.images360) {
      setView360(item);
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: "#f8f9fa", minHeight: "80vh" }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/gallery')}
            sx={{
              color: "#1a237e",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
          >
            Back to Gallery
          </Button>
        </Box>

        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#1a237e",
              mb: 2,
              background: "linear-gradient(45deg, #1a237e 30%, #3f51b5 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "#666",
              mb: 2,
              fontWeight: "300",
            }}
          >
            {subtitle}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#777",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>

          {/* Feature Highlights */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 4, flexWrap: "wrap" }}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <feature.icon sx={{ color: "#1a237e" }} />
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Categories Grid */}
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: "bold",
            color: "#1a237e",
            mb: 4,
            textAlign: "center",
          }}
        >
          {categoryType} Categories
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 4,
          }}
        >
          {categoryData.map((item) => (
            <Box key={item.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 24px rgba(26, 35, 126, 0.15)",
                  },
                  borderRadius: "16px",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={() => setSelectedItem(item)}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: 200,
                    background: getCategoryGradient(gradientCategory),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" mb={1}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {item.primaryUse[0]}
                    </Typography>
                  </Box>
                </CardMedia>

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    {item.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                    sx={{ lineHeight: 1.5 }}
                  >
                    {item.description}
                  </Typography>

                  {/* Key Characteristics */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                    {item.keyCharacteristics.slice(0, 3).map((char: string, charIndex: number) => (
                      <Typography
                        key={charIndex}
                        variant="caption"
                        sx={{
                          backgroundColor: "#f0f2ff",
                          color: "#1a237e",
                          px: 1,
                          py: 0.5,
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                        }}
                      >
                        {char}
                      </Typography>
                    ))}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem(item);
                      }}
                      sx={{
                        borderColor: "#1a237e",
                        color: "#1a237e",
                        "&:hover": {
                          backgroundColor: "#f0f2ff",
                        },
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<WhatsAppIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsApp(`${item.name} ${categoryType} inquiry`);
                      }}
                      sx={{
                        backgroundColor: "#25d366",
                        "&:hover": {
                          backgroundColor: "#1eb855",
                        },
                      }}
                    >
                      Inquiry
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Detailed Item Modal */}
        <Dialog
          open={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 768}
          PaperProps={{
            sx: { 
              borderRadius: { xs: 0, md: "16px" },
              maxHeight: { xs: '100vh', md: '90vh' },
              margin: { xs: 0, md: '32px' }
            },
          }}
        >
          {selectedItem && (
            <>
              <DialogTitle
                sx={{
                  background: getCategoryGradient(gradientCategory),
                  color: "white",
                  position: "relative",
                  px: { xs: 2, md: 3 },
                  pt: { xs: 2, md: 3 },
                  pb: { xs: 2, md: 3 }
                }}
              >
                <Typography 
                  variant="h5" 
                  fontWeight="bold"
                  sx={{ 
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    pr: { xs: 6, md: 0 }
                  }}
                >
                  {selectedItem.name}
                </Typography>
                <IconButton
                  onClick={() => setSelectedItem(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent 
                dividers 
                sx={{ 
                  p: 0,
                  overflowY: 'auto',
                  maxHeight: { xs: 'calc(100vh - 220px)', md: 'none' }
                }}
              >
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                  gap: { xs: 2, md: 3 }, 
                  p: { xs: 2, md: 3 }
                }}>
                  {/* Left Column */}
                  <Box>
                    <Box
                      sx={{
                        height: { xs: 200, md: 300 },
                        background: getCategoryGradient(gradientCategory),
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        mb: 3,
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <IconComponent sx={{ fontSize: "4rem", mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {selectedItem.name}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Key Characteristics
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                      {selectedItem.keyCharacteristics.map((char: string, index: number) => (
                        <Chip
                          key={index}
                          label={char}
                          size="small"
                          sx={{
                            backgroundColor: "#f0f2ff",
                            color: "#1a237e",
                          }}
                        />
                      ))}
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Materials & Construction
                    </Typography>
                    <List dense>
                      {selectedItem.commonMaterials.map((material: string, index: number) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={material}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Right Column */}
                  <Box>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Description
                    </Typography>
                    <Typography variant="body1" mb={3} sx={{ lineHeight: 1.6 }}>
                      {selectedItem.description}
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Primary Applications
                    </Typography>
                    <List dense>
                      {selectedItem.primaryUse.map((use: string, index: number) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={use}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Specifications
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" mb={1}>
                        <strong>Material:</strong> {selectedItem.specifications.material}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Installation:</strong> {selectedItem.specifications.installation}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Maintenance:</strong> {selectedItem.specifications.maintenance}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Warranty:</strong> {selectedItem.warranty}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Use Cases Section */}
                <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
                  <Typography variant="h6" fontWeight="bold" mb={3}>
                    Recommended Use Cases
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {selectedItem.useCases.map((useCase: any, index: number) => (
                      <Paper key={index} sx={{ p: 2, borderRadius: "8px" }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                          {useCase.location}
                        </Typography>
                        <Typography variant="body2" mb={2} sx={{ lineHeight: 1.5 }}>
                          {useCase.recommendation}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Key Checks:</strong> {useCase.keyChecks.join(", ")}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ 
                p: { xs: 2, md: 3 }, 
                gap: { xs: 1, md: 1 },
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                flexWrap: 'wrap',
                maxWidth: '100%'
              }}>
                <Button
                  onClick={() =>
                    handleShare({
                      title: selectedItem.name,
                      description: selectedItem.description,
                    })
                  }
                  startIcon={<ShareIcon />}
                  fullWidth={window.innerWidth < 768}
                  sx={{ 
                    color: "#666",
                    minHeight: { xs: '48px', md: '36px' },
                    fontSize: { xs: '0.9rem', md: '0.875rem' },
                    flex: { sm: '1 1 auto' },
                    maxWidth: { sm: '120px', md: 'none' },
                    border: { xs: '1px solid #e0e0e0', md: 'none' }
                  }}
                >
                  Share
                </Button>
                {selectedItem.images360 && (
                  <Button
                    onClick={() => handle360View(selectedItem)}
                    startIcon={<ThreeSixtyIcon />}
                    variant="outlined"
                    fullWidth={window.innerWidth < 768}
                    sx={{
                      borderColor: "#1a237e",
                      color: "#1a237e",
                      minHeight: { xs: '48px', md: '36px' },
                      fontSize: { xs: '0.9rem', md: '0.875rem' },
                      flex: { sm: '1 1 auto' },
                      maxWidth: { sm: '140px', md: 'none' }
                    }}
                  >
                    360° VIEW
                  </Button>
                )}
                <Button
                  onClick={() => handleWhatsApp(`${selectedItem.name} ${categoryType} inquiry`)}
                  startIcon={<WhatsAppIcon />}
                  variant="contained"
                  fullWidth={window.innerWidth < 768}
                  sx={{
                    backgroundColor: "#25d366",
                    minHeight: { xs: '48px', md: '36px' },
                    fontSize: { xs: '0.9rem', md: '0.875rem' },
                    flex: { sm: '1 1 auto' },
                    maxWidth: { sm: '160px', md: 'none' },
                    "&:hover": {
                      backgroundColor: "#1eb855",
                    },
                  }}
                >
                  WHATSAPP INQUIRY
                </Button>
                <Button
                  onClick={handleCall}
                  startIcon={<PhoneIcon />}
                  variant="contained"
                  fullWidth={window.innerWidth < 768}
                  sx={{
                    backgroundColor: "#1a237e",
                    minHeight: { xs: '48px', md: '36px' },
                    fontSize: { xs: '0.9rem', md: '0.875rem' },
                    flex: { sm: '1 1 auto' },
                    maxWidth: { sm: '130px', md: 'none' },
                    "&:hover": {
                      backgroundColor: "#0d47a1",
                    },
                  }}
                >
                  CALL NOW
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* 360 View Modal */}
        <Dialog
          open={!!view360}
          onClose={() => setView360(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              backgroundColor: "#000",
              height: "90vh",
            },
          }}
        >
          {view360 && (
            <>
              <DialogTitle sx={{ color: "white", textAlign: "center", position: "relative", p: 2 }}>
                <Typography variant="h5" component="h2" fontWeight="bold">
                  {view360.name} - 360° Interactive View
                </Typography>
                <IconButton
                  onClick={() => setView360(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, backgroundColor: "#000", height: "100%" }}>
                <TileViewer360
                  tileName={view360.name}
                  tileImages={view360.images360}
                  onClose={() => setView360(null)}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default CategoryPageTemplate;
